import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { useCreatePropertyGroup } from '@/admin/hooks/use-property-groups'
import { Button } from '@/shared/components/ui/button'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'

import { toContactInput, toGroupAddress } from '@/admin/lib/property-group-forms'

const FORM_ID = 'new-group-form'

/**
 * Create a group: its name, mailing address and first contact. More contacts,
 * properties and documents are added on the group's own page afterwards.
 */
export function NewGroupPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createGroup = useCreatePropertyGroup()

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: t('groups.new.name', '{{groupNoun}} Name'),
        value: '',
        required: t('groups.new.nameRequired', '{{groupNoun}} name is required.'),
        autoFocus: true,
      },
      {
        type: 'address',
        name: 'mailing_address',
        label: t('groups.new.mailingAddress', '{{groupNoun}} Mailing Address'),
        value: null,
        required: t('groups.new.mailingAddressRequired', 'Mailing address is required.'),
        configurationData: { lookup_provider: 'address' },
      },
      {
        type: 'text',
        name: 'first_name',
        label: t('groups.new.contactFirstName', '{{groupNoun}} Contact First Name'),
        value: '',
        required: t('groups.new.contactFirstNameRequired', 'First name is required.'),
      },
      {
        type: 'text',
        name: 'last_name',
        label: t('groups.new.contactLastName', '{{groupNoun}} Contact Last Name'),
        value: '',
        required: t('groups.new.contactLastNameRequired', 'Last name is required.'),
      },
      {
        type: 'email',
        name: 'email',
        label: t('groups.new.contactEmail', '{{groupNoun}} Contact Email'),
        value: '',
        hint: t('groups.new.emailOrPhone', 'Email or phone is required.'),
      },
      {
        type: 'phone',
        name: 'phone',
        label: t('groups.new.contactPhone', '{{groupNoun}} Contact Phone'),
        value: '',
      },
    ],
    [t],
  )

  const { Form } = useForm({ name: FORM_ID, submit: 'footer', fields }, async (values) => {
    const v = values as Record<string, unknown>
    try {
      const group = await createGroup.mutateAsync({
        name: String(v.name ?? '').trim(),
        mailing_address: toGroupAddress(v.mailing_address),
        contacts: [toContactInput(v)],
      })
      toast.success(t('groups.new.created', '{{groupNoun}} created'))
      navigate(`/groups/${group.uuid}`)
    } catch (err) {
      toastApiError(err, t('groups.new.createFailed', 'Could not create the {{groupNoun}}'))
    }
  })

  return (
    <AppShell>
      <main className="mx-auto max-w-2xl px-6 py-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/groups')}
          className="mb-4 cursor-pointer gap-1 px-0 text-[11px] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('groups.detail.back', 'All {{groupNounPlural}}')}
        </Button>
        <h1 className="clara-title mb-1">{t('groups.new.title', 'Add New {{groupNoun}}')}</h1>
        <p className="mb-8 text-[12px] text-[var(--ink-3)]">
          {t(
            'groups.new.description',
            'Start with the {{groupNoun}} and one contact. You add more contacts, its properties and its bylaws on the {{groupNoun}} page next.',
          )}
        </p>
        <Form className="flex flex-col gap-4" />
        <div className="mt-8 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/groups')}
            className="cursor-pointer"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={createGroup.isPending}
            className="cursor-pointer gap-1.5"
          >
            {createGroup.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('groups.new.submit', 'Create {{groupNoun}}')}
          </Button>
        </div>
      </main>
    </AppShell>
  )
}
